export type WidgetLang = 'en' | 'ru';

export interface TranslationCatalog {
  triggerLabel: string;
  backButtonAriaLabel: string;
  policyText: string;
  privacyPolicyLabel: string;
  inputPlaceholder: string;
  sendMessageAriaLabel: string;
  defaultFirstMessage: string;
  fallbackRecommendations: string[];
  authPromptMessage: string;
  authNamePlaceholder: string;
  authPhonePlaceholder: string;
  authAgreementLabel: string;
  authSubmitLabel: string;
  authNameError: string;
  authPhoneError: string;
  authAgreementError: string;
  authFieldRequiredError: string;
  authEmailError: string;
  authNumberError: string;
  authSelectPlaceholder: string;
  sendMessageFailed: string;
  recommendationsScrollLeftLabel: string;
  recommendationsScrollRightLabel: string;
  welcomeAssistantName: string;
  welcomeNowLabel: string;
  welcomeShowMoreLabel: string;
  welcomeCloseAriaLabel: string;
  contactFormCloseAriaLabel: string;
}

export const WIDGET_TRANSLATIONS: Record<WidgetLang, TranslationCatalog> = {
  en: {
    triggerLabel: 'Ask AI',
    backButtonAriaLabel: 'Back',
    policyText: 'By sending data, you agree to the {{privacyPolicy}}',
    privacyPolicyLabel: 'Privacy policy',
    inputPlaceholder: 'Ask any question',
    sendMessageAriaLabel: 'Send message',
    defaultFirstMessage: 'Hi! I’m an AI assistant. How can I help you?',
    fallbackRecommendations: ['Yes', "I'm interested", 'Help me'],
    authPromptMessage:
      'Leave your contact details so we can continue the conversation and follow up if needed.',
    authNamePlaceholder: 'Name',
    authPhonePlaceholder: 'Phone',
    authAgreementLabel: 'I agree to the terms',
    authSubmitLabel: 'Submit',
    authNameError: 'Name must be longer than 1 character',
    authPhoneError: 'Enter a valid phone number',
    authAgreementError: 'You must agree to the terms',
    authFieldRequiredError: 'This field is required',
    authEmailError: 'Enter a valid email address',
    authNumberError: 'Enter a valid number',
    authSelectPlaceholder: 'Select an option',
    sendMessageFailed: 'Failed to send the message. Please try again.',
    recommendationsScrollLeftLabel: 'Scroll recommendations left',
    recommendationsScrollRightLabel: 'Scroll recommendations right',
    welcomeAssistantName: 'AI assistant',
    welcomeNowLabel: 'now',
    welcomeShowMoreLabel: 'Show more',
    welcomeCloseAriaLabel: 'Close welcome block',
    contactFormCloseAriaLabel: 'Close contact form',
  },
  ru: {
    triggerLabel: 'Спросить ИИ',
    backButtonAriaLabel: 'Назад',
    policyText: 'При отправке данных вы соглашаетесь с документом {{privacyPolicy}}',
    privacyPolicyLabel: 'Политика конфиденциальности',
    inputPlaceholder: 'Задайте любой вопрос',
    sendMessageAriaLabel: 'Отправить сообщение',
    defaultFirstMessage: 'Здравствуйте! Я ИИ-ассистент. Чем могу помочь?',
    fallbackRecommendations: ['Да', 'Мне интересно', 'Помоги мне'],
    authPromptMessage:
      'Оставьте контактные данные, чтобы мы могли продолжить диалог и связаться с вами при необходимости.',
    authNamePlaceholder: 'Имя',
    authPhonePlaceholder: 'Телефон',
    authAgreementLabel: 'Я согласен с условиями',
    authSubmitLabel: 'Отправить',
    authNameError: 'Имя должно быть длиннее 1 символа',
    authPhoneError: 'Введите корректный номер телефона',
    authAgreementError: 'Необходимо согласиться с условиями',
    authFieldRequiredError: 'Заполните обязательное поле',
    authEmailError: 'Введите корректный email',
    authNumberError: 'Введите корректное число',
    authSelectPlaceholder: 'Выберите вариант',
    sendMessageFailed: 'Не удалось отправить сообщение. Попробуйте еще раз.',
    recommendationsScrollLeftLabel: 'Прокрутить рекомендации влево',
    recommendationsScrollRightLabel: 'Прокрутить рекомендации вправо',
    welcomeAssistantName: 'ИИ-ассистент',
    welcomeNowLabel: 'сейчас',
    welcomeShowMoreLabel: 'Показать больше',
    welcomeCloseAriaLabel: 'Закрыть приветственный блок',
    contactFormCloseAriaLabel: 'Закрыть форму контактов',
  },
};

export const resolveWidgetLang = (language?: string): WidgetLang =>
  language?.toLowerCase().startsWith('en') ? 'en' : 'ru';

export const getWidgetTranslations = (language?: string): TranslationCatalog =>
  WIDGET_TRANSLATIONS[resolveWidgetLang(language)];
